import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AdminGuard } from 'app/core/auth/guards/admin.guard';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { roleGuard } from 'app/core/auth/guards/role.guard';
import { signedInRedirectGuard } from 'app/core/auth/guards/signed-in-redirect.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
export const appRoutes: Route[] = [

    // Landing del usuario autenticado.
    //
    // El destino depende del rol y el rol se resuelve de forma asincrónica
    // (`GET auth/me`), así que no alcanza con un `redirectTo` estático: lo
    // decide `signedInRedirectGuard`, que siempre devuelve un UrlTree. El
    // componente está solo para que la ruta sea activable; nunca se pinta.
    //
    // Issue #17: el tutor cae en `/dashboard`; el resto de los roles sigue
    // yendo a `/example` como hasta ahora.
    {path: '', pathMatch : 'full', canActivate: [signedInRedirectGuard], component: LayoutComponent, data: {layout: 'empty'}},

    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', canActivate: [signedInRedirectGuard], component: LayoutComponent, data: {layout: 'empty'}},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            // El sign-up de ejemplo de Fuse se borró (issue #69): el registro
            // es el de tutor. La ruta queda para que un link viejo no caiga en 404.
            {path: 'sign-up', pathMatch: 'full', redirectTo: 'tutor-registration'},
            {path: 'tutor-registration', loadChildren: () => import('app/modules/tutor/registration/tutor-registration.routes')},
        ]
    },

    // Family registration route - requires authentication
    {
        path: 'family-registration',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        loadChildren: () => import('app/modules/auth/family-registration/family-registration.routes')
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'choose-profile', loadChildren: () => import('app/modules/auth/choose-profile/choose-profile.routes')},
            {path: 'set-password', loadChildren: () => import('app/modules/auth/set-password/set-password.routes')},
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'admin/example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'admin/content', loadChildren: () => import('app/modules/admin/content-management/content-management.routes')},
            {path: 'admin/courses', loadChildren: () => import('app/modules/admin/courses/courses.routes')},
            {path: 'admin/subjects', loadChildren: () => import('app/modules/admin/subjects/subjects.routes')},
            {path: 'admin/modules', loadChildren: () => import('app/modules/admin/modules/modules.routes')},
            {path: 'admin/lessons', loadChildren: () => import('app/modules/admin/lessons/lessons.routes')},
            {path: 'admin/students', canActivate: [AdminGuard], loadChildren: () => import('app/modules/admin/students/students.routes')},
            {path: 'admin/tutors', loadChildren: () => import('app/modules/admin/tutors/tutors.routes')},
            {path: 'admin/package-templates', loadChildren: () => import('app/modules/admin/package-templates/package-templates.routes')},
            {path: 'admin/payments', loadChildren: () => import('app/modules/admin/payments/payments.routes')},
            {path: 'admin/useful-links', loadChildren: () => import('app/modules/admin/useful-links/useful-links.routes')},
            {path: 'admin/audit-log', canActivate: [AdminGuard], loadChildren: () => import('app/modules/admin/audit-log/audit-log.routes')},
            {path: 'admin/report-cards', canActivate: [AdminGuard], loadChildren: () => import('app/modules/admin/report-cards/report-cards.routes')},

            // Rutas viejas del Super Admin (issue #13). El rol desapareció y
            // las pantallas se fusionaron, pero hay links y favoritos
            // apuntando acá: redirigen, nunca 404.
            {path: 'admin/super-admin/students/:id', redirectTo: 'admin/students/:id'},
            {path: 'admin/super-admin/students', redirectTo: 'admin/students', pathMatch: 'full'},
            {path: 'admin/super-admin/audit-log', redirectTo: 'admin/audit-log', pathMatch: 'full'},
            {path: 'access-denied', loadChildren: () => import('app/modules/error/access-denied/access-denied.routes')},
            // Pantalla de inicio del tutor (issue #17). `roleGuard` restringe
            // la ruta a los roles que hoy comparten los endpoints de tutor.
            {path: 'dashboard', canActivate: [roleGuard(['parent', 'teacher'])], loadChildren: () => import('app/modules/tutor/dashboard/tutor-dashboard.routes')},
            {path: 'tutor/students', loadChildren: () => import('app/modules/tutor/students/tutor-students.routes')},
            // Asistente del boletín oficial de la familia (issue #28)
            {path: 'tutor/report-cards', canActivate: [roleGuard(['parent'])], loadChildren: () => import('app/modules/tutor/report-cards/tutor-report-cards.routes')},
            {path: 'student/courses', loadChildren: () => import('app/modules/student/courses/student-courses.routes')},
            {path: 'student/report-card', loadChildren: () => import('app/modules/student/report-card/student-report-card.routes')},
            {path: 'student/useful-links', loadChildren: () => import('app/modules/student/useful-links/student-useful-links.routes')},
            {path: 'ai-chat', loadChildren: () => import('app/modules/ai-chat/ai-chat.routes')},
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
        ]
    }
];
